import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { Flame, Users, ListPlus, Minus, Plus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useMangalCalculator } from "@/lib/api";
import { useOrderList } from "@/context/OrderListContext";
import { formatPrice } from "@/lib/site";

// gram → okunabilir miktar metni (sipariş listesi formatına uygun)
const formatQty = (grams) => {
  if (grams >= 1000) {
    const kg = grams / 1000;
    return Number.isInteger(kg) ? `${kg} kg` : `${kg.toFixed(1)} kg`;
  }
  return `${grams} gr`;
};

// "Mangal Keyfi" senaryosu içine gömülen kişi-bazlı hesaplayıcı.
export const MangalPlanner = ({ onAdded }) => {
  const { data, isLoading } = useMangalCalculator();
  const { addItems, setIsOpen } = useOrderList();
  const [people, setPeople] = useState(4);

  const rows = useMemo(() => {
    if (!data?.per_person) return [];
    return data.per_person.map((it) => {
      const grams = it.grams * people;
      const est = it.price != null ? (it.price * grams) / 1000 : null;
      return { ...it, grams, quantity: formatQty(grams), est };
    });
  }, [data, people]);

  const totalGrams = rows.reduce((s, r) => s + r.grams, 0);
  const totalEst = rows.reduce((s, r) => s + (r.est || 0), 0);

  const addAll = () => {
    addItems(
      rows.map((r) => ({
        slug: r.slug, name: r.name, image: r.image,
        price: r.price ?? null, unit: r.unit || "kg",
        quantity: r.quantity, note: `Mangal · ${people} kişilik`,
      }))
    );
    toast.success(`${people} kişilik mangal listene eklendi`, {
      description: `${rows.length} ürün, toplam ~${formatQty(totalGrams)} et sipariş listene aktarıldı.`,
    });
    onAdded?.();
    setIsOpen(true);
  };

  if (isLoading || !data) return null;

  return (
    <div data-testid="mangal-planner" className="px-4 py-5 sm:px-6">
      {/* Kaç kişisiniz? */}
      <div className="border border-dashed border-copper/30 bg-stone p-5">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-copper">Kaç kişisiniz?</span>
        <div className="mt-3 flex items-end gap-2">
          <span data-testid="mangal-people-count" className="font-serif text-5xl leading-none text-cream">{people}</span>
          <span className="mb-1 font-mono text-xs uppercase tracking-wider text-cream/50">kişi</span>
        </div>
        <div className="mt-5 flex items-center gap-4">
          <button
            data-testid="mangal-people-minus"
            onClick={() => setPeople((p) => Math.max(2, p - 1))}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center border border-copper/40 text-copper transition-colors hover:bg-copper hover:text-charcoal"
          >
            <Minus className="h-4 w-4" />
          </button>
          <Slider
            data-testid="mangal-people-slider"
            value={[people]}
            min={2}
            max={16}
            step={1}
            onValueChange={([v]) => setPeople(v)}
            className="flex-1"
          />
          <button
            data-testid="mangal-people-plus"
            onClick={() => setPeople((p) => Math.min(16, p + 1))}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center border border-copper/40 text-copper transition-colors hover:bg-copper hover:text-charcoal"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Önerilen ürünler */}
      <div className="mt-5">
        <div className="mb-3 flex items-center justify-between border-b border-dashed border-copper/25 pb-2.5">
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/55">
            <Flame className="h-4 w-4 text-copper" /> Önerilen Mangal Sofrası
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/55">Miktar</span>
        </div>
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <li key={r.slug} data-testid={`mangal-row-${r.slug}`} className="flex items-center gap-3">
              <img src={r.image} alt={r.name} className="h-11 w-11 flex-shrink-0 object-cover" />
              <span className="flex-1 font-serif text-lg leading-tight text-cream">{r.name}</span>
              <span data-testid={`mangal-qty-${r.slug}`} className="font-mono text-base font-bold text-copper">{r.quantity}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5 flex items-center gap-3 border-t border-dashed border-copper/25 pt-4">
        <Users className="h-5 w-5 text-copper" />
        <p className="text-sm text-cream/65">
          Toplam <span className="font-bold text-cream">~{formatQty(totalGrams)}</span> et
          {totalEst > 0 && (
            <> · Tahmini <span className="font-bold text-cream">{formatPrice(Math.round(totalEst))}</span></>
          )}
        </p>
      </div>

      <button
        data-testid="mangal-add-all"
        onClick={addAll}
        className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-sm bg-bordo px-5 py-3.5 text-base font-bold uppercase tracking-wide text-cream transition-colors hover:bg-bordo-dark"
      >
        <ListPlus className="h-5 w-5" /> Tümünü Sipariş Listeme Ekle
      </button>
    </div>
  );
};

export default MangalPlanner;
